export function makeBatchId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `batch_${crypto.randomUUID()}`;
  }
  return `batch_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export function sanitizeRunFolderName(jsonFileName) {
  const base = String(jsonFileName || "").replace(/\.json$/i, "");
  const cleaned = base
    .replace(/[^A-Za-z0-9_-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  return cleaned ? `Run_${cleaned}` : "Run_route";
}

// The Route token is the path label embedded in the route's JSON name
// (LasSeparationPath_<token>.json) and prefixed onto that route's lane XML
// filenames (<token>_...). Used to show only a route's own XMLs.
export function routeTokenFromJsonName(jsonName) {
  return String(jsonName || "")
    .replace(/^LasSeparationPath_/i, "")
    .replace(/\.json$/i, "")
    .trim();
}

// Filter the XML pool to only the files belonging to a route (by token prefix).
export function xmlsForRoute(xmlFiles, jsonName) {
  const token = routeTokenFromJsonName(jsonName);
  if (!token) return [];
  const prefix = `${token}_`.toLowerCase();
  return (xmlFiles || []).filter((f) =>
    String(f?.name || "")
      .toLowerCase()
      .startsWith(prefix),
  );
}

// Parse a lane XML filename into its meaningful parts. Format (route token and
// state are stripped — they're constant per route/project):
//   <routeToken>_<STATE...>_<routeNumber>_<segment>_<lane>_<direction>.xml
// Parsed from the END so a multi-word STATE (e.g. DEMO_TEST) doesn't matter.
export function parseLaneXmlName(name) {
  const base = String(name || "").replace(/\.xml$/i, "");
  const parts = base.split("_").filter((p) => p !== "");
  if (parts.length < 5) {
    return { routeNumber: base, segment: "", lane: "", direction: "", raw: name };
  }
  return {
    direction: parts[parts.length - 1],
    lane: parts[parts.length - 2],
    segment: parts[parts.length - 3],
    routeNumber: parts[parts.length - 4],
    raw: name,
  };
}

export function buildRunSpecs(routes, selectedTasksByRoute, routesMatrixSelection, xmlFiles, taskToService, normalizeKey) {
  const specs = [];
  for (const route of routes || []) {
    const tasks = (selectedTasksByRoute || {})[route.jsonId] || [];
    const routeXmls = xmlsForRoute(xmlFiles, route.jsonName);
    const xmlsByTask = {};
    const taskSet = new Set();
    for (const task of tasks) {
      const picked = routeXmls.filter((x) => {
        const key = normalizeKey(x.googleDriveId);
        return (routesMatrixSelection || {})?.[key]?.[task];
      });
      if (picked.length > 0) {
        xmlsByTask[task] = picked;
        taskSet.add(task);
      }
    }
    if (taskSet.size > 0) {
      specs.push({
        jsonId: route.jsonId,
        jsonName: route.jsonName,
        runFolderName: sanitizeRunFolderName(route.jsonName),
        tasks: Array.from(taskSet),
        xmlsByTask,
      });
    }
  }
  return specs;
}

export function normalizeFolderCode(name) {
  return String(name || "").toUpperCase().replace(/[^A-Z0-9]+/g, "");
}

const FOLDER_MIME = "application/vnd.google-apps.folder";

export function pathFoldersFromRunFolderChildren(items, rsaFolderCodeToSuffix) {
  const out = [];
  for (const item of items || []) {
    // The google-drive-contents endpoint returns an `isFolder` boolean and does
    // NOT include `mimeType`; keep the mimeType check as a fallback for callers
    // that pass raw Drive items.
    const isFolder =
      item?.isFolder === true || (item?.mimeType || "") === FOLDER_MIME;
    if (!isFolder) continue;
    const code = normalizeFolderCode(item?.name);
    const suffix = (rsaFolderCodeToSuffix || {})[code];
    if (!suffix) continue;
    const id = String(item?.googleDriveId ?? item?.id ?? "").trim();
    if (!id) continue;
    out.push({ config_type: suffix, path_folder_id: id });
  }
  return out.sort((a, b) => a.config_type.localeCompare(b.config_type));
}

export function buildRouteAnalysisMetrics(runSpec, {
  perFileMileage,
  perFileManeuvers,
  taskToService,
  isdService,
  normalizeFileNameKey,
  projectId,
  generatedAt,
}) {
  const mileage_by_service = {};
  const maneuvers_by_service = {};
  const route_count_by_service = {};

  for (const task of runSpec.tasks || []) {
    const service = (taskToService || {})[task];
    if (!service) continue;
    if (!(service in mileage_by_service)) {
      mileage_by_service[service] = 0;
      maneuvers_by_service[service] = 0;
      route_count_by_service[service] = 0;
    }
    for (const xml of (runSpec.xmlsByTask || {})[task] || []) {
      const key = normalizeFileNameKey(xml.name);
      const miles = Number((perFileMileage || {})[key] ?? (perFileMileage || {})[xml.name]);
      const man = Number((perFileManeuvers || {})[key] ?? (perFileManeuvers || {})[xml.name]);
      route_count_by_service[service] += 1;
      if (service === isdService) {
        if (Number.isFinite(man)) maneuvers_by_service[service] += man;
      } else {
        if (Number.isFinite(miles)) mileage_by_service[service] += miles;
      }
    }
  }

  const mileage_total_miles = Object.values(mileage_by_service).reduce((s, v) => s + v, 0);
  const maneuvers_total = Object.values(maneuvers_by_service).reduce((s, v) => s + v, 0);
  const route_total = Object.values(route_count_by_service).reduce((s, v) => s + v, 0);

  return {
    mileage_by_service,
    maneuvers_by_service,
    route_count_by_service,
    mileage_total_miles,
    maneuvers_total,
    route_total,
    selected_project_id: projectId,
    metrics_generated_at: generatedAt,
  };
}
